import apiCall from './api';
import modelService from './modelService';

/**
 * ✅ ATELIER: Service pour la gestion des commandes atelier.
 */
const customOrderService = {

  // ============================================================
  // ============== CONSTANTES (pour UI) =======================
  // ============================================================

  STATUSES: [
    { value: 'RECEIVED', label: 'Reçue', icon: '🟡', color: 'secondary' },
    { value: 'CUTTING', label: 'Coupe', icon: '✂️', color: 'info' },
    { value: 'SEWING', label: 'Couture', icon: '🧵', color: 'primary' },
    { value: 'FITTING', label: 'Essayage', icon: '👗', color: 'warning' },
    { value: 'READY', label: 'Prête', icon: '✅', color: 'success' },
    { value: 'DELIVERED', label: 'Livrée', icon: '📦', color: 'dark' },
    { value: 'CANCELLED', label: 'Annulée', icon: '❌', color: 'danger' }
  ],

  PRIORITIES: [
    { value: 'NORMAL', label: 'Normal', icon: '🔵', color: 'info' },
    { value: 'URGENT', label: 'Urgent', icon: '🔴', color: 'danger' }
  ],

  /**
   * Retourne les statuts suivants possibles depuis un statut donné.
   */
  getAvailableNextStatuses(currentStatus) {
    if (!currentStatus) return [];

    // Commandes finalisées : aucune transition
    if (currentStatus === 'DELIVERED' || currentStatus === 'CANCELLED') {
      return [];
    }

    const order = ['RECEIVED', 'CUTTING', 'SEWING', 'FITTING', 'READY', 'DELIVERED'];
    const currentIdx = order.indexOf(currentStatus);

    // Statuts suivants dans le workflow
    const next = currentIdx >= 0 ? order.slice(currentIdx + 1) : [];

    // L'annulation est toujours possible si non finalisée
    return [...next, 'CANCELLED'];
  },

  /**
   * Récupère les infos d'affichage d'un statut.
   */
  getStatusInfo(status) {
    return this.STATUSES.find(s => s.value === status)
      || { value: status, label: status, icon: '❓', color: 'secondary' };
  },

  /**
   * Récupère les infos d'affichage d'une priorité.
   */
  getPriorityInfo(priority) {
    return this.PRIORITIES.find(p => p.value === priority)
      || { value: priority, label: priority, icon: '⚪', color: 'secondary' };
  },

  // ============================================================
  // ============== APPELS API ==================================
  // ============================================================

  /**
   * Récupérer les commandes (paginé).
   */
  async getOrders({ page = 0, size = 20, status = null, search = null } = {}) {
    try {
      const params = { page, size };
      if (status) params.status = status;
      if (search && search.trim()) params.search = search.trim();

      const response = await apiCall.get('/atelier/orders', params);

      if (response.success) {
        // Le backend renvoie un Page<CustomOrderDto>
        const data = response.data || {};
        return {
          success: true,
          data: {
            content: data.content || [],
            totalElements: data.totalElements || 0,
            totalPages: data.totalPages || 0,
            currentPage: data.number || 0,
            size: data.size || size
          }
        };
      }

      return { success: false, data: { content: [] }, message: response.message };

    } catch (error) {
      console.error('❌ Erreur récupération commandes:', error);
      return {
        success: false,
        data: { content: [] },
        message: error.message || 'Erreur de connexion'
      };
    }
  },

  /**
   * Récupérer les commandes actives (non livrées ni annulées).
   */
  async getActiveOrders() {
    try {
      const response = await apiCall.get('/atelier/orders/active');
      if (response.success) {
        return { success: true, data: Array.isArray(response.data) ? response.data : [] };
      }
      return { success: false, data: [] };
    } catch (error) {
      console.error('❌ Erreur:', error);
      return { success: false, data: [], message: error.message };
    }
  },

  /**
   * Livraisons à venir dans les N prochains jours.
   */
  async getUpcomingDeliveries(days = 7) {
    try {
      const response = await apiCall.get('/atelier/orders/upcoming-deliveries', { days });
      if (response.success) {
        return { success: true, data: Array.isArray(response.data) ? response.data : [] };
      }
      return { success: false, data: [] };
    } catch (error) {
      console.error('❌ Erreur:', error);
      return { success: false, data: [], message: error.message };
    }
  },

  /**
   * Commandes en retard.
   */
  async getOverdueOrders() {
    try {
      const response = await apiCall.get('/atelier/orders/overdue');
      if (response.success) {
        return { success: true, data: Array.isArray(response.data) ? response.data : [] };
      }
      return { success: false, data: [] };
    } catch (error) {
      console.error('❌ Erreur:', error);
      return { success: false, data: [], message: error.message };
    }
  },

  /**
   * Commandes d'une cliente.
   */
  async getOrdersByCustomer(customerId) {
    try {
      const response = await apiCall.get(`/atelier/orders/customer/${customerId}`);
      if (response.success) {
        return { success: true, data: Array.isArray(response.data) ? response.data : [] };
      }
      return { success: false, data: [] };
    } catch (error) {
      console.error('❌ Erreur:', error);
      return { success: false, data: [], message: error.message };
    }
  },

  /**
   * Détail d'une commande.
   */
  async getOrderById(orderId) {
    try {
      const response = await apiCall.get(`/atelier/orders/${orderId}`);
      return response;
    } catch (error) {
      console.error('❌ Erreur:', error);
      return { success: false, message: error.message };
    }
  },

  /**
   * Créer une commande.
   */
  async createOrder(orderData) {
    try {
      // Validation minimale frontend
      if (!orderData.customerId) {
        return { success: false, message: 'La cliente est obligatoire' };
      }
      if (!orderData.totalPrice || parseFloat(orderData.totalPrice) < 0) {
        return { success: false, message: 'Le prix total est invalide' };
      }
      if (!orderData.expectedDeliveryDate) {
        return { success: false, message: 'La date de livraison est obligatoire' };
      }
      if (!orderData.modelId && !orderData.customModelName?.trim()) {
        return {
          success: false,
          message: 'Vous devez choisir un modèle du catalogue OU saisir un nom de modèle'
        };
      }

      const payload = this._buildCreatePayload(orderData);
      console.log('🧵 Création commande avec payload:', payload);

      const response = await apiCall.post('/atelier/orders', payload);
      return response;

    } catch (error) {
      console.error('❌ Erreur création commande:', error);
      return {
        success: false,
        message: error.message || 'Erreur de connexion'
      };
    }
  },

  // ============================================================
  // ============== UPLOAD PHOTO TISSU ==========================
  // ============================================================

  /**
   * Validation locale d'un fichier image avant upload.
   * Mêmes règles que côté serveur (5MB max, JPG/PNG/WebP).
   */
  validateImageFile(file) {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      return {
        isValid: false,
        message: 'Type non supporté. Utilisez JPG, PNG ou WebP'
      };
    }
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      return {
        isValid: false,
        message: 'Fichier trop volumineux (max 5MB)'
      };
    }
    return { isValid: true };
  },

  /**
   * ✅ NOUVEAU: Uploader la photo du tissu pour une commande existante.
   * À appeler APRÈS la création de la commande (on a besoin de l'orderId).
   *
   * @param {string} orderId - L'ID de la commande
   * @param {File} imageFile - Le fichier image à uploader
   * @returns {Promise<{success, data: string (url), message}>}
   */
  async uploadFabricImage(orderId, imageFile) {
    try {
      console.log('🧵 Upload tissu commande:', orderId, imageFile?.name);

      // Validation locale
      const validation = this.validateImageFile(imageFile);
      if (!validation.isValid) {
        return { success: false, message: validation.message };
      }

      const formData = new FormData();
      formData.append('image', imageFile);

      const response = await apiCall.upload(
        `/atelier/orders/${orderId}/upload-fabric-image`,
        formData
      );

      if (response.success) {
        console.log('✅ Tissu uploadé:', response.data);
        return {
          success: true,
          data: response.data, // l'URL relative
          message: response.message || 'Photo du tissu enregistrée'
        };
      }

      return {
        success: false,
        message: response.message || 'Erreur lors de l\'upload'
      };

    } catch (error) {
      console.error('❌ Erreur upload tissu:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Erreur de connexion'
      };
    }
  },

  /**
   * ✅ NOUVEAU: Supprimer la photo du tissu d'une commande.
   */
  async deleteFabricImage(orderId) {
    try {
      console.log('🗑️ Suppression tissu commande:', orderId);

      const response = await apiCall.delete(`/atelier/orders/${orderId}/fabric-image`);
      return response;

    } catch (error) {
      console.error('❌ Erreur suppression tissu:', error);
      return {
        success: false,
        message: error.message || 'Erreur de connexion'
      };
    }
  },

  /**
   * ✅ NOUVEAU: Récupérer l'URL absolue de la photo du tissu d'une commande.
   * Utilise la même logique que modelService.buildImageUrl pour construire l'URL prod.
   *
   * @param {object} order - L'objet commande
   * @returns {string|null} URL absolue ou null
   */
  getFabricImageAbsoluteUrl(order) {
    if (!order?.fabricImageUrl) return null;
    // Réutilisation de modelService.buildImageUrl (déjà importé en haut du fichier)
    return modelService.buildImageUrl(order.fabricImageUrl);
  },

  /**
   * Mettre à jour une commande.
   */
  async updateOrder(orderId, orderData) {
    try {
      const payload = this._buildUpdatePayload(orderData);
      const response = await apiCall.put(`/atelier/orders/${orderId}`, payload);
      return response;
    } catch (error) {
      console.error('❌ Erreur mise à jour:', error);
      return { success: false, message: error.message || 'Erreur de connexion' };
    }
  },

  /**
   * Changer le statut d'une commande.
   */
  async changeStatus(orderId, newStatus, reason = null) {
    try {
      const payload = { status: newStatus };
      if (reason) payload.reason = reason;

      const response = await apiCall.patch(
        `/atelier/orders/${orderId}/status`,
        payload
      );
      return response;
    } catch (error) {
      console.error('❌ Erreur changement statut:', error);
      return { success: false, message: error.message || 'Erreur de connexion' };
    }
  },

  /**
   * Ajouter un paiement.
   */
  async addPayment(orderId, paymentData) {
    try {
      if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
        return { success: false, message: 'Le montant est invalide' };
      }
      if (!paymentData.paymentMethod) {
        return { success: false, message: 'La méthode de paiement est obligatoire' };
      }

      const payload = {
        amount: parseFloat(paymentData.amount),
        paymentMethod: paymentData.paymentMethod,
        paymentType: paymentData.paymentType || null,
        referenceNumber: paymentData.referenceNumber?.trim() || null,
        notes: paymentData.notes?.trim() || null
      };

      const response = await apiCall.post(`/atelier/orders/${orderId}/payments`, payload);
      return response;
    } catch (error) {
      console.error('❌ Erreur ajout paiement:', error);
      return { success: false, message: error.message || 'Erreur de connexion' };
    }
  },

  /**
   * Supprimer un paiement (Manager uniquement).
   */
  async deletePayment(paymentId) {
    try {
      const response = await apiCall.delete(`/atelier/orders/payments/${paymentId}`);
      return response;
    } catch (error) {
      console.error('❌ Erreur suppression paiement:', error);
      return { success: false, message: error.message || 'Erreur de connexion' };
    }
  },

  // ============================================================
  // ============== UTILITAIRES D'AFFICHAGE =====================
  // ============================================================

  /**
   * Formater un prix en FCFA.
   */
  formatPrice(price) {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0
    }).format(price || 0);
  },

  /**
   * Formater une date courte (jj/mm/aaaa).
   */
  formatDate(dateString) {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  },

  /**
   * Formater une date + heure.
   */
  formatDateTime(dateString) {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  },

  /**
   * Formater les jours restants avant livraison.
   */
  formatDaysUntil(days) {
    if (days === null || days === undefined) return '—';
    if (days < 0) return `En retard de ${Math.abs(days)} jour${Math.abs(days) > 1 ? 's' : ''}`;
    if (days === 0) return "Aujourd'hui";
    if (days === 1) return 'Demain';
    return `Dans ${days} jours`;
  },

  // ============================================================
  // ============== PRIVATE HELPERS =============================
  // ============================================================

  _buildCreatePayload(data) {
  const payload = {
    customerId: data.customerId,
    measurementId: data.measurementId || null,
    modelId: data.modelId || null,
    customModelName: data.customModelName?.trim() || null,
    customModelImageUrl: data.customModelImageUrl?.trim() || null,
    fabricDescription: data.fabricDescription?.trim() || null,
    fabricProvidedByCustomer: data.fabricProvidedByCustomer !== false,
    totalPrice: parseFloat(data.totalPrice),
    expectedDeliveryDate: data.expectedDeliveryDate,
    priority: data.priority || 'NORMAL',
    notes: data.notes?.trim() || null
  };

  // ✅ NOUVEAU : Remise (optionnelle)
  if (data.discountType && data.discountValue && parseFloat(data.discountValue) > 0) {
    payload.discountType = data.discountType;
    payload.discountValue = parseFloat(data.discountValue);
    console.log('💰 Remise dans payload:', payload.discountType, payload.discountValue);
  }

  // Avance initiale (existant)
  const advance = data.initialAdvanceAmount
    ? parseFloat(data.initialAdvanceAmount) : 0;

  if (advance > 0) {
    payload.initialAdvanceAmount = advance;
    payload.initialAdvancePaymentMethod = data.initialAdvancePaymentMethod;
    payload.initialAdvanceReference = data.initialAdvanceReference?.trim() || null;
  } else {
    payload.initialAdvanceAmount = 0;
  }

  return payload;
},

  _buildUpdatePayload(data) {
    const payload = {};

    if (data.measurementId !== undefined) payload.measurementId = data.measurementId || null;
    if (data.modelId !== undefined) payload.modelId = data.modelId || null;
    if (data.customModelName !== undefined) {
      payload.customModelName = data.customModelName?.trim() || null;
    }
    if (data.customModelImageUrl !== undefined) {
      payload.customModelImageUrl = data.customModelImageUrl?.trim() || null;
    }
    if (data.fabricDescription !== undefined) {
      payload.fabricDescription = data.fabricDescription?.trim() || null;
    }
    if (data.fabricProvidedByCustomer !== undefined) {
      payload.fabricProvidedByCustomer = data.fabricProvidedByCustomer;
    }
    if (data.totalPrice !== undefined && data.totalPrice !== '') {
      payload.totalPrice = parseFloat(data.totalPrice);
    }
    if (data.expectedDeliveryDate !== undefined) {
      payload.expectedDeliveryDate = data.expectedDeliveryDate;
    }
    if (data.priority !== undefined) payload.priority = data.priority;
    if (data.notes !== undefined) payload.notes = data.notes?.trim() || null;

    return payload;
  },

  /**
 * ✅ Récupérer les stats agrégées du dashboard atelier.
 */
async getDashboard() {
  try {
    const response = await apiCall.get('/atelier/dashboard');
    if (response.success) {
      return { success: true, data: response.data };
    }
    return { success: false, data: null, message: response.message };
  } catch (error) {
    console.error('❌ Erreur dashboard:', error);
    return { 
      success: false, 
      data: null, 
      message: error.message || 'Erreur de connexion' 
    };
  }
}
};

export default customOrderService;